import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    FaUsers, FaUserCheck, FaCreditCard, FaShieldAlt,
    FaPassport, FaSearch, FaFileExport, FaSyncAlt,
    FaSignOutAlt, FaKey, FaExternalLinkAlt, FaCheckCircle,
    FaTimesCircle, FaHourglassHalf, FaPrint, FaTimes,
    FaSpinner, FaChartPie, FaRupeeSign, FaDollarSign, FaUser, FaDownload, FaFilePdf
} from "react-icons/fa";
import {
    getAdminUsers,
    getAdminPaidUsers,
    getAdminPayments,
    getAdminAuditStamps,
    getAdminParticipants,
    getAdminStats,
    logoutUser,
    handleSessionExpired,
    downloadParticipantPdfApi
} from "../../../service/register.service";
import { generateReceiptPdf } from "../../../utils/receiptPdfGenerator";
import { generateParticipantPdf } from "../../../utils/participantPdfGenerator";
import ChangePasswordModal from "../../common/ChangePasswordModal";
import Pagination from "../../common/Pagination";
import styles from "./AdminDashboardPage.module.css";

const AdminDashboardPage = () => {
    const navigate = useNavigate();

    // Active tab: 'overview' | 'users' | 'paid-users' | 'payments' | 'audit-stamps' | 'participants'
    const [activeTab, setActiveTab] = useState("overview");

    // Data states
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [paidUsers, setPaidUsers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [auditStamps, setAuditStamps] = useState([]);
    const [participants, setParticipants] = useState([]);

    // Filter & Search states
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("ALL");

    // Client-side UI pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reset pagination to first page whenever search or filter or tab changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm, filterCategory]);

    const handleRowsPerPageChange = (newSize) => {
        setRowsPerPage(newSize);
        setCurrentPage(1);
    };

    // Modal state for viewing details
    const [selectedItem, setSelectedItem] = useState(null);
    const [modalType, setModalType] = useState(null); // 'user' | 'payment' | 'participant'
    const [changePassOpen, setChangePassOpen] = useState(false);

    // Current Admin User Details from localStorage
    const adminUser = localStorage.getItem("ivec_user") || "admin";
    const adminDisplayName = localStorage.getItem("ivec_display_name") || adminUser;

    // Security Verification on Mount
    useEffect(() => {
        const token = localStorage.getItem("ivec_token");
        const role = localStorage.getItem("ivec_role");

        if (!token) {
            navigate("/login");
            return;
        }

        if (role !== "ROLE_ADMIN") {
            Swal.fire({
                icon: "warning",
                title: "Access Restricted",
                text: "Admin privileges are required to access this dashboard.",
                confirmButtonColor: "#2b53a3"
            });
            navigate("/payment");
            return;
        }

        fetchAllData();
    }, [navigate]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [
                statsRes,
                usersRes,
                paidRes,
                paymentsRes,
                auditRes,
                partRes
            ] = await Promise.all([
                getAdminStats().catch(() => null),
                getAdminUsers().catch(() => ({ data: [] })),
                getAdminPaidUsers().catch(() => ({ data: [] })),
                getAdminPayments().catch(() => ({ data: [] })),
                getAdminAuditStamps().catch(() => ({ data: [] })),
                getAdminParticipants().catch(() => ({ data: [] })),
            ]);

            if (statsRes?.data) setStats(statsRes.data);
            if (usersRes?.data) setUsers(usersRes.data);
            if (paidRes?.data) setPaidUsers(paidRes.data);
            if (paymentsRes?.data) setPayments(paymentsRes.data);
            if (auditRes?.data) setAuditStamps(auditRes.data);
            if (partRes?.data) setParticipants(partRes.data);

        } catch (err) {
            console.error("Failed to fetch admin data:", err);
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                await handleSessionExpired();
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        Swal.fire({
            title: "Sign Out",
            text: "Are you sure you want to end your admin session?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2b53a3",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Sign Out",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await logoutUser();
                } catch (e) { }
                localStorage.removeItem("ivec_token");
                localStorage.removeItem("ivec_user");
                localStorage.removeItem("ivec_role");
                localStorage.removeItem("ivec_display_name");
                navigate("/login");
            }
        });
    };

    // CSV Exporter Helper
    const exportCsv = (filename, rows) => {
        if (!rows || !rows.length) {
            Swal.fire({ icon: "info", title: "No Data", text: "There is no data to export." });
            return;
        }
        const separator = ",";
        const keys = Object.keys(rows[0]);
        const csvContent =
            keys.join(separator) +
            "\n" +
            rows.map((row) =>
                keys.map((k) => {
                    let cell = row[k] === null || row[k] === undefined ? "" : row[k];
                    cell = cell instanceof Date ? cell.toLocaleString() : cell.toString().replace(/"/g, '""');
                    if (cell.search(/("|,|\n)/g) >= 0) cell = `"${cell}"`;
                    return cell;
                }).join(separator)
            ).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filtered Registered Users
    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            const matchesSearch =
                (u.userName && u.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.displayName && u.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.mobile && u.mobile.includes(searchTerm));

            if (!matchesSearch) return false;
            if (filterCategory === "INDIAN") return u.delegateType?.toLowerCase() === "indian";
            if (filterCategory === "FOREIGN") return u.delegateType?.toLowerCase() === "foreign";
            if (filterCategory === "PAID") return u.hasPaid;
            if (filterCategory === "UNPAID") return !u.hasPaid;
            return true;
        });
    }, [users, searchTerm, filterCategory]);

    // Filtered Paid Users
    const filteredPaidUsers = useMemo(() => {
        return paidUsers.filter((u) => {
            return (
                (u.userName && u.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.displayName && u.displayName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.razorpayOrderId && u.razorpayOrderId.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (u.razorpayPaymentId && u.razorpayPaymentId.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        });
    }, [paidUsers, searchTerm]);

    // Filtered Payments
    const filteredPayments = useMemo(() => {
        return payments.filter((p) => {
            const matchesSearch =
                (p.userEmail && p.userEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.delegateName && p.delegateName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.razorpayOrderId && p.razorpayOrderId.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.razorpayPaymentId && p.razorpayPaymentId.toLowerCase().includes(searchTerm.toLowerCase()));

            if (!matchesSearch) return false;
            if (filterCategory !== "ALL" && p.paymentStatus !== filterCategory) return false;
            return true;
        });
    }, [payments, searchTerm, filterCategory]);

    // Filtered Audit Logs
    const filteredAudits = useMemo(() => {
        return auditStamps.filter((a) => {
            const matchesSearch =
                (a.userName && a.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (a.ipAddress && a.ipAddress.includes(searchTerm)) ||
                (a.details && a.details.toLowerCase().includes(searchTerm.toLowerCase()));

            if (!matchesSearch) return false;
            if (filterCategory !== "ALL" && a.activityType !== filterCategory) return false;
            return true;
        });
    }, [auditStamps, searchTerm, filterCategory]);

    // Filtered Participants
    const filteredParticipants = useMemo(() => {
        return participants.filter((p) => {
            const matchesSearch =
                (p.fullName && p.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.passportNumber && p.passportNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.nationality && p.nationality.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.organization && p.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (p.emailId && p.emailId.toLowerCase().includes(searchTerm.toLowerCase()));

            if (!matchesSearch) return false;
            return true;
        });
    }, [participants, searchTerm]);

    // Client-side sliced datasets for UI pagination
    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredUsers.slice(start, start + rowsPerPage);
    }, [filteredUsers, currentPage, rowsPerPage]);

    const paginatedPaidUsers = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredPaidUsers.slice(start, start + rowsPerPage);
    }, [filteredPaidUsers, currentPage, rowsPerPage]);

    const paginatedPayments = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredPayments.slice(start, start + rowsPerPage);
    }, [filteredPayments, currentPage, rowsPerPage]);

    const paginatedAudits = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredAudits.slice(start, start + rowsPerPage);
    }, [filteredAudits, currentPage, rowsPerPage]);

    const paginatedParticipants = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredParticipants.slice(start, start + rowsPerPage);
    }, [filteredParticipants, currentPage, rowsPerPage]);

    const formatDateTime = (dt) => {
        if (!dt) return "—";
        try {
            return new Date(dt).toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
            });
        } catch {
            return String(dt);
        }
    };

    const formatDateOnly = (d) => {
        if (!d) return "—";
        try {
            return new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" });
        } catch {
            return String(d);
        }
    };

    const handleReceiptPrint = (item) => {
        if (!item) {
            Swal.fire({
                icon: "warning",
                title: "Receipt Unavailable",
                text: "No payment record was selected.",
                confirmButtonColor: "#2b53a3",
            });
            return;
        }

        const availablePayments = Array.isArray(payments) ? payments : [];
        const paidUserData =
            (item.razorpayOrderId &&
                availablePayments.find(
                    (payment) =>
                        payment?.paymentStatus === "SUCCESS" &&
                        payment.razorpayOrderId === item.razorpayOrderId
                )) ||
            (item.razorpayPaymentId &&
                availablePayments.find(
                    (payment) =>
                        payment?.paymentStatus === "SUCCESS" &&
                        payment.razorpayPaymentId === item.razorpayPaymentId
                )) ||
            availablePayments.find(
                (payment) =>
                    payment?.paymentStatus === "SUCCESS" &&
                    item.loginId &&
                    payment.loginId === item.loginId
            );

        const paymentRecord = paidUserData || item;
        if (paymentRecord.paymentStatus !== "SUCCESS") {
            Swal.fire({
                icon: "warning",
                title: "Receipt Unavailable",
                text: "No confirmed payment record was found to generate a receipt.",
                confirmButtonColor: "#2b53a3",
            });
            return;
        }

        const userData = {
            displayName: item.displayName || item.delegateName,
            userName: item.userName,
            email: item.email || item.userEmail,
            countryCode: item.countryCode,
            mobile: item.mobile || item.mobileNumber,
            affiliation: item.affiliation || item.organization,
            passportNo: item.passportNo,
            aadhaarNo: item.aadhaarNo,
            ieeeMemberNo: item.ieeeMemberNo,
            nationality: item.nationality,
            delegateType: item.delegateType || paymentRecord.delegateType,
        };
        const paymentData = {
            delegateType: paymentRecord.delegateType ?? item.delegateType,
            currency: paymentRecord.currency ?? item.currency,
            razorpayPaymentId: paymentRecord.razorpayPaymentId ?? item.razorpayPaymentId,
            razorpayOrderId: paymentRecord.razorpayOrderId ?? item.razorpayOrderId,
            verifiedDate: paymentRecord.verifiedDate ?? item.verifiedDate ?? item.latestPaymentDate,
            ieeeMemberNo: paymentRecord.ieeeMemberNo ?? item.ieeeMemberNo,
            discountAmount: paymentRecord.discountAmount ?? item.discountAmount,
            baseAmount: paymentRecord.baseAmount ?? item.baseAmount,
            feeTier: paymentRecord.feeTier ?? item.feeTier,
            miniCourseAdded: paymentRecord.miniCourseAdded ?? item.miniCourseAdded,
            miniCourseAmount: paymentRecord.miniCourseAmount ?? item.miniCourseAmount,
            totalAmount: paymentRecord.totalAmount ?? item.totalAmount ?? item.paidAmount,
        };

        try {
            generateReceiptPdf(userData, paymentData);
        } catch (err) {
            console.error("PDF generation error:", err);
            Swal.fire({
                icon: "error",
                title: "PDF Error",
                text: "Could not generate the PDF receipt. Please try again.",
                confirmButtonColor: "#2b53a3",
            });
        }
    };

    const handleParticipantPdfDownload = async (participant) => {
        if (!participant) return;
        try {
            await downloadParticipantPdfApi(participant.id, participant.fullName);
        } catch (err) {
            console.warn("Backend PDF download failed, falling back to client-side generator:", err);
            try {
                generateParticipantPdf(participant, true);
            } catch (fallbackErr) {
                console.error("Client PDF generation fallback error:", fallbackErr);
                Swal.fire({
                    icon: "error",
                    title: "PDF Download Failed",
                    text: "Unable to generate participant clearance PDF. Please try again.",
                    confirmButtonColor: "#2b53a3"
                });
            }
        }
    };

    return (
        <div className={styles.pageWrapper}>
            {/* Top Navigation */}
            <header className={styles.topNav}>
                <div className={styles.topNavContainer}>
                    <div className={styles.brandWrap} onClick={() => navigate("/")}>
                        <div className={styles.brandLogo}>
                            <FaShieldAlt />
                        </div>
                        <div>
                            <h1 className={styles.brandTitle}>
                                IVEC 2027 <span className={styles.adminBadge}>Admin Console</span>
                            </h1>
                            <p className={styles.brandSub}>Conference Delegate &amp; Security Administration</p>
                        </div>
                    </div>

                    <div className={styles.navActions}>
                        <div className={styles.adminUserChip}>
                            <div className={styles.userAvatar}>
                                <FaUser />
                            </div>
                            <span>{adminDisplayName}</span>
                        </div>

                        <button
                            type="button"
                            className={styles.btnAction}
                            onClick={() => navigate("/payment")}
                            title="Open Delegate Payment View"
                        >
                            <FaExternalLinkAlt /> Delegate View
                        </button>

                        <button
                            type="button"
                            className={styles.btnAction}
                            onClick={() => setChangePassOpen(true)}
                            title="Change Password"
                        >
                            <FaKey /> Password
                        </button>

                        <button
                            type="button"
                            className={styles.btnLogout}
                            onClick={handleLogout}
                            title="Sign Out"
                        >
                            <FaSignOutAlt /> Sign Out
                        </button>
                    </div>
                </div>
            </header>

            {/* Navigation Tabs */}
            <nav className={styles.tabsBar}>
                <div className={styles.tabsContainer}>
                    <button
                        className={`${styles.tabBtn} ${activeTab === "overview" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("overview"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaChartPie /> Overview
                    </button>

                    <button
                        className={`${styles.tabBtn} ${activeTab === "users" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("users"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaUsers /> Registered Users
                        <span className={styles.tabCount}>{users.length}</span>
                    </button>

                    <button
                        className={`${styles.tabBtn} ${activeTab === "paid-users" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("paid-users"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaUserCheck /> Registered with Paid
                        <span className={styles.tabCount}>{paidUsers.length}</span>
                    </button>

                    <button
                        className={`${styles.tabBtn} ${activeTab === "payments" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("payments"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaCreditCard /> Payment History
                        <span className={styles.tabCount}>{payments.length}</span>
                    </button>

                    <button
                        className={`${styles.tabBtn} ${activeTab === "audit-stamps" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("audit-stamps"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaShieldAlt /> Audit Stamping List
                        <span className={styles.tabCount}>{auditStamps.length}</span>
                    </button>

                    <button
                        className={`${styles.tabBtn} ${activeTab === "participants" ? styles.tabBtnActive : ""}`}
                        onClick={() => { setActiveTab("participants"); setSearchTerm(""); setFilterCategory("ALL"); }}
                    >
                        <FaPassport /> Participant Info Details
                        <span className={styles.tabCount}>{participants.length}</span>
                    </button>
                </div>
            </nav>

            {/* Main Area */}
            <main className={styles.mainContainer}>
                {loading ? (
                    <div className={styles.stateBox}>
                        <FaSpinner className={styles.spinner} />
                        <p>Loading administration data…</p>
                    </div>
                ) : (
                    <>
                        {/* =========================================================
                            TAB 1: OVERVIEW & STATS
                        ========================================================= */}
                        {activeTab === "overview" && (
                            <div>
                                <div className={styles.statsGrid}>
                                    <div
                                        className={styles.statCard}
                                        onClick={() => { setActiveTab("users"); setFilterCategory("ALL"); }}
                                    >
                                        <div className={`${styles.statIconWrap} ${styles.statBlue}`}>
                                            <FaUsers />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Total Registered</span>
                                            <span className={styles.statValue}>{stats?.totalRegisteredUsers ?? users.length}</span>
                                            <span className={styles.statSub}>All accounts</span>
                                        </div>
                                    </div>

                                    <div
                                        className={styles.statCard}
                                        onClick={() => { setActiveTab("paid-users"); setFilterCategory("ALL"); }}
                                    >
                                        <div className={`${styles.statIconWrap} ${styles.statGreen}`}>
                                            <FaUserCheck />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Paid Delegates</span>
                                            <span className={styles.statValue}>{stats?.totalPaidUsers ?? paidUsers.length}</span>
                                            <span className={styles.statSub}>Confirmed payments</span>
                                        </div>
                                    </div>

                                    <div className={styles.statCard} onClick={() => setActiveTab("payments")}>
                                        <div className={`${styles.statIconWrap} ${styles.statAmber}`}>
                                            <FaRupeeSign />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Indian Revenue</span>
                                            <span className={styles.statValue}>
                                                ₹{(stats?.totalInrRevenue ?? 0).toLocaleString("en-IN")}
                                            </span>
                                            <span className={styles.statSub}>INR Collections</span>
                                        </div>
                                    </div>

                                    <div className={styles.statCard} onClick={() => setActiveTab("payments")}>
                                        <div className={`${styles.statIconWrap} ${styles.statIndigo}`}>
                                            <FaDollarSign />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Foreign Revenue</span>
                                            <span className={styles.statValue}>
                                                ${(stats?.totalUsdRevenue ?? 0).toLocaleString("en-US")}
                                            </span>
                                            <span className={styles.statSub}>USD Collections</span>
                                        </div>
                                    </div>

                                    <div className={styles.statCard} onClick={() => setActiveTab("participants")}>
                                        <div className={`${styles.statIconWrap} ${styles.statPurple}`}>
                                            <FaPassport />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Participant Forms</span>
                                            <span className={styles.statValue}>{stats?.totalParticipantClearances ?? participants.length}</span>
                                            <span className={styles.statSub}>Visa clearances</span>
                                        </div>
                                    </div>

                                    <div className={styles.statCard} onClick={() => setActiveTab("audit-stamps")}>
                                        <div className={`${styles.statIconWrap} ${styles.statBlue}`}>
                                            <FaShieldAlt />
                                        </div>
                                        <div className={styles.statInfo}>
                                            <span className={styles.statLabel}>Audit Logs</span>
                                            <span className={styles.statValue}>{stats?.totalAuditLogs ?? auditStamps.length}</span>
                                            <span className={styles.statSub}>Security events</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Recent Activity Preview */}
                                <div className="row g-4">
                                    <div className="col-12 col-lg-6">
                                        <div className={styles.tableCard}>
                                            <div className={styles.tableHeader}>
                                                <div className={styles.tableTitleWrap}>
                                                    <h3 className={styles.tableTitle}>Recent Registrations</h3>
                                                    <span className={styles.tableSubtitle}>Latest registered delegates</span>
                                                </div>
                                                <button
                                                    className={styles.btnAction}
                                                    style={{ color: "#2563eb", borderColor: "#cbd5e1" }}
                                                    onClick={() => setActiveTab("users")}
                                                >
                                                    View All
                                                </button>
                                            </div>
                                            <div className={styles.tableResponsive}>
                                                <table className={styles.customTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Delegate</th>
                                                            <th>Type</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {users.slice(0, 5).map((u) => (
                                                            <tr key={u.loginId}>
                                                                <td>
                                                                    <span className={styles.primaryText}>{u.displayName}</span>
                                                                    <span className={styles.subText}>{u.email}</span>
                                                                </td>
                                                                <td>
                                                                    <span className={u.delegateType?.toLowerCase() === "foreign" ? styles.pillTypeForeign : styles.pillTypeIndian}>
                                                                        {u.delegateType?.toUpperCase()}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {u.hasPaid ? (
                                                                        <span className={styles.pillPaid}><FaCheckCircle /> PAID</span>
                                                                    ) : (
                                                                        <span className={styles.pillUnpaid}><FaTimesCircle /> UNPAID</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-6">
                                        <div className={styles.tableCard}>
                                            <div className={styles.tableHeader}>
                                                <div className={styles.tableTitleWrap}>
                                                    <h3 className={styles.tableTitle}>Recent Security Logins &amp; Audits</h3>
                                                    <span className={styles.tableSubtitle}>Latest authentication activity</span>
                                                </div>
                                                <button
                                                    className={styles.btnAction}
                                                    style={{ color: "#2563eb", borderColor: "#cbd5e1" }}
                                                    onClick={() => setActiveTab("audit-stamps")}
                                                >
                                                    View All
                                                </button>
                                            </div>
                                            <div className={styles.tableResponsive}>
                                                <table className={styles.customTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>User / IP</th>
                                                            <th>Activity</th>
                                                            <th>Time</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {auditStamps.slice(0, 5).map((a) => (
                                                            <tr key={a.id}>
                                                                <td>
                                                                    <span className={styles.primaryText}>{a.userName || "ANONYMOUS"}</span>
                                                                    <span className={styles.subText}>{a.ipAddress}</span>
                                                                </td>
                                                                <td>
                                                                    <span className={
                                                                        a.activityType === "LOGIN_SUCCESS" ? styles.auditPillSuccess :
                                                                            a.activityType === "LOGIN_FAILED" ? styles.auditPillFailed :
                                                                                a.activityType === "LOGIN_RATE_LIMITED" ? styles.auditPillRateLimited :
                                                                                    a.activityType === "LOGOUT" ? styles.auditPillLogout :
                                                                                        styles.auditPillPassword
                                                                    }>
                                                                        {a.activityType}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className={styles.subText}>{formatDateTime(a.timestamp)}</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* =========================================================
                            TAB 2: REGISTERED USERS
                        ========================================================= */}
                        {activeTab === "users" && (
                            <div className={styles.tableCard}>
                                <div className={styles.tableHeader}>
                                    <div className={styles.tableTitleWrap}>
                                        <h2 className={styles.tableTitle}>Registered Users ({filteredUsers.length})</h2>
                                        <span className={styles.tableSubtitle}>
                                            Manage and monitor all conference registrations
                                        </span>
                                    </div>

                                    <div className={styles.controlsWrap}>
                                        <div className={styles.searchInputWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Search name, email, phone..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                        </div>

                                        <select
                                            className={styles.filterSelect}
                                            value={filterCategory}
                                            onChange={(e) => setFilterCategory(e.target.value)}
                                        >
                                            <option value="ALL">All Delegates</option>
                                            <option value="INDIAN">Indian Delegates</option>
                                            <option value="FOREIGN">Foreign Delegates</option>
                                            <option value="PAID">Paid Only</option>
                                            <option value="UNPAID">Unpaid Only</option>
                                        </select>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={() => exportCsv("ivec_registered_users.csv", filteredUsers)}
                                            title="Export CSV"
                                        >
                                            <FaFileExport /> Export CSV
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={fetchAllData}
                                            title="Refresh"
                                        >
                                            <FaSyncAlt />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableResponsive}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Delegate Name</th>
                                                <th>Email / Username</th>
                                                <th>Type</th>
                                                <th>Contact</th>
                                                <th>Reg Date</th>
                                                <th>Payment Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" style={{ textAlign: "center", padding: "2rem" }}>
                                                        No registered users found matching the filters.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedUsers.map((u) => (
                                                    <tr key={u.loginId}>
                                                        <td>
                                                            <span className={styles.primaryText}>{u.displayName}</span>
                                                            {u.affiliation && (
                                                                <span className={styles.subText}>{u.affiliation}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{u.email}</span>
                                                            <span className={styles.subText}>Role: {u.roles?.join(", ") || "ROLE_USER"}</span>
                                                        </td>
                                                        <td>
                                                            <span className={u.delegateType?.toLowerCase() === "foreign" ? styles.pillTypeForeign : styles.pillTypeIndian}>
                                                                {u.delegateType?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{u.countryCode} {u.mobile}</span>
                                                            {u.whatsappNo && (
                                                                <span className={styles.subText}>WA: {u.whatsappNo}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={styles.subText}>{formatDateTime(u.createdDate)}</span>
                                                        </td>
                                                        <td>
                                                            {u.hasPaid ? (
                                                                <span className={styles.pillPaid}>
                                                                    <FaCheckCircle /> PAID ({u.currency} {u.paidAmount})
                                                                </span>
                                                            ) : u.paymentStatus === "CREATED" ? (
                                                                <span className={styles.pillCreated}>
                                                                    <FaHourglassHalf /> PENDING
                                                                </span>
                                                            ) : (
                                                                <span className={styles.pillUnpaid}>
                                                                    <FaTimesCircle /> UNPAID
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className={styles.btnViewDetails}
                                                                onClick={() => {
                                                                    setSelectedItem(u);
                                                                    setModalType("user");
                                                                }}
                                                            >
                                                                View Info
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={filteredUsers.length}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={setCurrentPage}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </div>
                        )}

                        {/* =========================================================
                            TAB 3: REGISTERED WITH PAID
                        ========================================================= */}
                        {activeTab === "paid-users" && (
                            <div className={styles.tableCard}>
                                <div className={styles.tableHeader}>
                                    <div className={styles.tableTitleWrap}>
                                        <h2 className={styles.tableTitle}>Paid Registrations ({filteredPaidUsers.length})</h2>
                                        <span className={styles.tableSubtitle}>
                                            Delegates who have completed registration fee payments
                                        </span>
                                    </div>

                                    <div className={styles.controlsWrap}>
                                        <div className={styles.searchInputWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Search name, email, order ID..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={() => exportCsv("ivec_paid_users.csv", filteredPaidUsers)}
                                            title="Export CSV"
                                        >
                                            <FaFileExport /> Export CSV
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={fetchAllData}
                                            title="Refresh"
                                        >
                                            <FaSyncAlt />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableResponsive}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Delegate</th>
                                                <th>Email</th>
                                                <th>Category</th>
                                                <th>Amount Paid</th>
                                                <th>Razorpay Order ID</th>
                                                <th>Payment ID</th>
                                                <th>Payment Date</th>
                                                <th>Receipt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPaidUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" style={{ textAlign: "center", padding: "2rem" }}>
                                                        No paid registrations found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedPaidUsers.map((u) => (
                                                    <tr key={u.loginId}>
                                                        <td>
                                                            <span className={styles.primaryText}>{u.displayName}</span>
                                                            <span className={styles.subText}>{u.mobile}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{u.email}</span>
                                                        </td>
                                                        <td>
                                                            <span className={u.delegateType?.toLowerCase() === "foreign" ? styles.pillTypeForeign : styles.pillTypeIndian}>
                                                                {u.delegateType?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <strong style={{ color: "#15803d" }}>
                                                                {u.currency} {u.paidAmount}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <code style={{ fontSize: "0.8rem", color: "#334155" }}>
                                                                {u.razorpayOrderId || "—"}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            <code style={{ fontSize: "0.8rem", color: "#2563eb" }}>
                                                                {u.razorpayPaymentId || "—"}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            <span className={styles.subText}>{formatDateTime(u.latestPaymentDate)}</span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className={styles.btnViewDetails}
                                                                onClick={() => {
                                                                    setSelectedItem(u);
                                                                    setModalType("payment");
                                                                }}
                                                            >
                                                                Receipt
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={filteredPaidUsers.length}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={setCurrentPage}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </div>
                        )}

                        {/* =========================================================
                            TAB 4: PAYMENT HISTORY
                        ========================================================= */}
                        {activeTab === "payments" && (
                            <div className={styles.tableCard}>
                                <div className={styles.tableHeader}>
                                    <div className={styles.tableTitleWrap}>
                                        <h2 className={styles.tableTitle}>Payment Transaction History ({filteredPayments.length})</h2>
                                        <span className={styles.tableSubtitle}>
                                            All attempted, completed, and failed transactions
                                        </span>
                                    </div>

                                    <div className={styles.controlsWrap}>
                                        <div className={styles.searchInputWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Search email, order, pay ID..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                        </div>

                                        <select
                                            className={styles.filterSelect}
                                            value={filterCategory}
                                            onChange={(e) => setFilterCategory(e.target.value)}
                                        >
                                            <option value="ALL">All Statuses</option>
                                            <option value="SUCCESS">SUCCESS</option>
                                            <option value="FAILED">FAILED</option>
                                            <option value="CREATED">CREATED</option>
                                        </select>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={() => exportCsv("ivec_payment_transactions.csv", filteredPayments)}
                                            title="Export CSV"
                                        >
                                            <FaFileExport /> Export CSV
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={fetchAllData}
                                            title="Refresh"
                                        >
                                            <FaSyncAlt />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableResponsive}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Order ID</th>
                                                <th>Payment ID</th>
                                                <th>Delegate</th>
                                                <th>Fee Tier</th>
                                                <th>Mini-Course</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPayments.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" style={{ textAlign: "center", padding: "2rem" }}>
                                                        No payment transactions found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedPayments.map((p) => (
                                                    <tr key={p.paymentId}>
                                                        <td>
                                                            <code style={{ fontSize: "0.8rem", color: "#0f172a" }}>
                                                                {p.razorpayOrderId}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            <code style={{ fontSize: "0.8rem", color: "#2563eb" }}>
                                                                {p.razorpayPaymentId || "—"}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{p.delegateName}</span>
                                                            <span className={styles.subText}>{p.userEmail}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText} style={{ textTransform: "capitalize" }}>
                                                                {p.feeTier}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {p.miniCourseAdded ? (
                                                                <span style={{ color: "#15803d", fontWeight: "600", fontSize: "0.8rem" }}>
                                                                    ✓ Yes
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>No</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <strong>
                                                                {p.currency} {p.totalAmount}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            {p.paymentStatus === "SUCCESS" ? (
                                                                <span className={styles.pillPaid}><FaCheckCircle /> SUCCESS</span>
                                                            ) : p.paymentStatus === "FAILED" ? (
                                                                <span className={styles.pillUnpaid}><FaTimesCircle /> FAILED</span>
                                                            ) : (
                                                                <span className={styles.pillCreated}><FaHourglassHalf /> CREATED</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={styles.subText}>{formatDateTime(p.createdDate)}</span>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={filteredPayments.length}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={setCurrentPage}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </div>
                        )}

                        {/* =========================================================
                            TAB 5: AUDIT STAMPING LIST
                        ========================================================= */}
                        {activeTab === "audit-stamps" && (
                            <div className={styles.tableCard}>
                                <div className={styles.tableHeader}>
                                    <div className={styles.tableTitleWrap}>
                                        <h2 className={styles.tableTitle}>Security Audit Logs ({filteredAudits.length})</h2>
                                        <span className={styles.tableSubtitle}>
                                            Authentication events, IP addresses, timestamps, and session status
                                        </span>
                                    </div>

                                    <div className={styles.controlsWrap}>
                                        <div className={styles.searchInputWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Search IP, username, details..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                        </div>

                                        <select
                                            className={styles.filterSelect}
                                            value={filterCategory}
                                            onChange={(e) => setFilterCategory(e.target.value)}
                                        >
                                            <option value="ALL">All Activities</option>
                                            <option value="LOGIN_SUCCESS">LOGIN_SUCCESS</option>
                                            <option value="LOGIN_FAILED">LOGIN_FAILED</option>
                                            <option value="LOGIN_RATE_LIMITED">LOGIN_RATE_LIMITED</option>
                                            <option value="LOGOUT">LOGOUT</option>
                                            <option value="SESSION_EXPIRED">SESSION_EXPIRED</option>
                                            <option value="PASSWORD_CHANGE">PASSWORD_CHANGE</option>
                                            <option value="PASSWORD_RESET">PASSWORD_RESET</option>
                                        </select>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={() => exportCsv("ivec_audit_stamping_logs.csv", filteredAudits)}
                                            title="Export CSV"
                                        >
                                            <FaFileExport /> Export CSV
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={fetchAllData}
                                            title="Refresh"
                                        >
                                            <FaSyncAlt />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableResponsive}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Timestamp</th>
                                                <th>User / Login ID</th>
                                                <th>Activity Type</th>
                                                <th>Client IP</th>
                                                <th>Status</th>
                                                <th>Details</th>
                                                <th>User Agent / Browser</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAudits.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" style={{ textAlign: "center", padding: "2rem" }}>
                                                        No audit records found matching the filters.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedAudits.map((a) => (
                                                    <tr key={a.id}>
                                                        <td>
                                                            <span className={styles.primaryText} style={{ fontSize: "0.82rem" }}>
                                                                {formatDateTime(a.timestamp)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{a.userName || "ANONYMOUS"}</span>
                                                            {a.loginId && (
                                                                <span className={styles.subText} style={{ fontFamily: "monospace" }}>
                                                                    ID: {a.loginId.substring(0, 8)}…
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={
                                                                a.activityType === "LOGIN_SUCCESS" ? styles.auditPillSuccess :
                                                                    a.activityType === "LOGIN_FAILED" ? styles.auditPillFailed :
                                                                        a.activityType === "LOGIN_RATE_LIMITED" ? styles.auditPillRateLimited :
                                                                            a.activityType === "LOGOUT" ? styles.auditPillLogout :
                                                                                a.activityType === "SESSION_EXPIRED" ? styles.auditPillExpired :
                                                                                    styles.auditPillPassword
                                                            }>
                                                                {a.activityType}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <code style={{ fontSize: "0.82rem", background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px" }}>
                                                                {a.ipAddress || "UNKNOWN"}
                                                            </code>
                                                        </td>
                                                        <td>
                                                            {a.status === "SUCCESS" ? (
                                                                <span className={styles.pillPaid}><FaCheckCircle /> OK</span>
                                                            ) : (
                                                                <span className={styles.pillUnpaid}><FaTimesCircle /> FAILED</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span style={{ fontSize: "0.82rem" }}>{a.details}</span>
                                                        </td>
                                                        <td style={{ maxWidth: "220px" }}>
                                                            <span
                                                                className={styles.subText}
                                                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                                title={a.userAgent}
                                                            >
                                                                {a.userAgent || "—"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={filteredAudits.length}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={setCurrentPage}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </div>
                        )}

                        {/* =========================================================
                            TAB 6: PARTICIPANT INFO DETAILS
                        ========================================================= */}
                        {activeTab === "participants" && (
                            <div className={styles.tableCard}>
                                <div className={styles.tableHeader}>
                                    <div className={styles.tableTitleWrap}>
                                        <h2 className={styles.tableTitle}>Visa Participant Clearances ({filteredParticipants.length})</h2>
                                        <span className={styles.tableSubtitle}>
                                            International participant submissions for MEA / MHA conference clearance
                                        </span>
                                    </div>

                                    <div className={styles.controlsWrap}>
                                        <div className={styles.searchInputWrap}>
                                            <FaSearch className={styles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Search name, passport, country..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={() => exportCsv("ivec_participant_clearances.csv", filteredParticipants)}
                                            title="Export CSV"
                                        >
                                            <FaFileExport /> Export CSV
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.btnExport}
                                            onClick={fetchAllData}
                                            title="Refresh"
                                        >
                                            <FaSyncAlt />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.tableResponsive}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Participant Name</th>
                                                <th>Nationality</th>
                                                <th>Passport No</th>
                                                <th>Designation</th>
                                                <th>Organization</th>
                                                <th>Email &amp; Mobile</th>
                                                <th>Submission Date</th>
                                                <th>Clearance Dossier / PDF</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredParticipants.length === 0 ? (
                                                <tr>
                                                    <td colSpan="8" style={{ textAlign: "center", padding: "2rem" }}>
                                                        No participant clearance records found.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedParticipants.map((p) => (
                                                    <tr key={p.id}>
                                                        <td>
                                                            <span className={styles.primaryText}>{p.fullName}</span>
                                                            <span className={styles.subText}>Birth: {p.countryOfBirth}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.pillTypeForeign}>{p.nationality}</span>
                                                        </td>
                                                        <td>
                                                            <code style={{ fontSize: "0.85rem", fontWeight: "700", color: "#1e3a8a" }}>
                                                                {p.passportNumber}
                                                            </code>
                                                            <span className={styles.subText}>
                                                                Exp: {formatDateOnly(p.dateOfExpiry)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{p.designation}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{p.organization}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.primaryText}>{p.emailId}</span>
                                                            <span className={styles.subText}>{p.mobileNumber}</span>
                                                        </td>
                                                        <td>
                                                            <span className={styles.subText}>{formatDateTime(p.createdDate)}</span>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.btnPdfDownload}
                                                                    onClick={() => handleParticipantPdfDownload(p)}
                                                                    title="Download Official Clearance Form (PDF)"
                                                                >
                                                                    <FaFilePdf /> PDF
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.btnViewDetails}
                                                                    onClick={() => {
                                                                        setSelectedItem(p);
                                                                        setModalType("participant");
                                                                    }}
                                                                    title="View Clearance Dossier"
                                                                >
                                                                    Full Dossier
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalItems={filteredParticipants.length}
                                    rowsPerPage={rowsPerPage}
                                    onPageChange={setCurrentPage}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Change Password Modal */}
            <ChangePasswordModal
                isOpen={changePassOpen}
                onClose={() => setChangePassOpen(false)}
            />

            {/* Detail Viewer Modal */}
            {selectedItem && (
                <div className={styles.modalOverlay} onClick={() => setSelectedItem(null)}>
                    <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {modalType === "user" && `Delegate Details: ${selectedItem.displayName}`}
                                {modalType === "payment" && `Payment Receipt: ${selectedItem.displayName || selectedItem.delegateName}`}
                                {modalType === "participant" && `Visa Clearance Dossier: ${selectedItem.fullName}`}
                            </h3>
                            <button
                                className={styles.closeBtn}
                                onClick={() => setSelectedItem(null)}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.modalBody} id="printable-detail">
                            {/* USER MODAL */}
                            {modalType === "user" && (
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Full Name</span>
                                        <span className={styles.detailValue}>{selectedItem.displayName}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Email ID</span>
                                        <span className={styles.detailValue}>{selectedItem.email}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Delegate Category</span>
                                        <span className={styles.detailValue}>{selectedItem.delegateType?.toUpperCase()}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Contact Number</span>
                                        <span className={styles.detailValue}>{selectedItem.countryCode} {selectedItem.mobile}</span>
                                    </div>
                                    {selectedItem.whatsappNo && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>WhatsApp Number</span>
                                            <span className={styles.detailValue}>{selectedItem.whatsappNo}</span>
                                        </div>
                                    )}
                                    {selectedItem.aadhaarNo && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Aadhaar Number</span>
                                            <span className={styles.detailValue}>{selectedItem.aadhaarNo}</span>
                                        </div>
                                    )}
                                    {selectedItem.passportNo && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Passport Number</span>
                                            <span className={styles.detailValue}>{selectedItem.passportNo}</span>
                                        </div>
                                    )}
                                    {selectedItem.placeOfIssue && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Place of Issue</span>
                                            <span className={styles.detailValue}>{selectedItem.placeOfIssue}</span>
                                        </div>
                                    )}
                                    {selectedItem.nationality && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Nationality</span>
                                            <span className={styles.detailValue}>{selectedItem.nationality}</span>
                                        </div>
                                    )}
                                    {selectedItem.countryOfBirth && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Country of Birth</span>
                                            <span className={styles.detailValue}>{selectedItem.countryOfBirth}</span>
                                        </div>
                                    )}
                                    {selectedItem.affiliation && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>Affiliation / Organization</span>
                                            <span className={styles.detailValue}>{selectedItem.affiliation}</span>
                                        </div>
                                    )}
                                    {selectedItem.ieeeMemberNo && (
                                        <div className={styles.detailItem}>
                                            <span className={styles.detailLabel}>IEEE Member Number</span>
                                            <span className={styles.detailValue}>{selectedItem.ieeeMemberNo}</span>
                                        </div>
                                    )}
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Payment Status</span>
                                        <span className={styles.detailValue}>
                                            {selectedItem.hasPaid ? `PAID (${selectedItem.currency} ${selectedItem.paidAmount})` : "UNPAID"}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Registration Date</span>
                                        <span className={styles.detailValue}>{formatDateTime(selectedItem.createdDate)}</span>
                                    </div>
                                </div>
                            )}

                            {/* PAYMENT MODAL */}
                            {modalType === "payment" && (
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Delegate Name</span>
                                        <span className={styles.detailValue}>{selectedItem.displayName || selectedItem.delegateName}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Email Address</span>
                                        <span className={styles.detailValue}>{selectedItem.email || selectedItem.userEmail}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Razorpay Order ID</span>
                                        <span className={styles.detailValue}>{selectedItem.razorpayOrderId}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Razorpay Payment ID</span>
                                        <span className={styles.detailValue} style={{ color: "#2563eb" }}>
                                            {selectedItem.razorpayPaymentId}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Amount Paid</span>
                                        <span className={styles.detailValue} style={{ color: "#15803d", fontSize: "1.1rem" }}>
                                            {selectedItem.currency} {selectedItem.paidAmount || selectedItem.totalAmount}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Payment Status</span>
                                        <span className={styles.detailValue}>{selectedItem.paymentStatus || "SUCCESS"}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Payment Date</span>
                                        <span className={styles.detailValue}>{formatDateTime(selectedItem.latestPaymentDate || selectedItem.verifiedDate)}</span>
                                    </div>
                                </div>
                            )}

                            {/* PARTICIPANT MODAL */}
                            {modalType === "participant" && (
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Full Name (as per Passport)</span>
                                        <span className={styles.detailValue}>{selectedItem.fullName}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Designation / Role</span>
                                        <span className={styles.detailValue}>{selectedItem.designation}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Organization / Institution</span>
                                        <span className={styles.detailValue}>{selectedItem.organization}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Nationality / Citizenship</span>
                                        <span className={styles.detailValue}>{selectedItem.nationality}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Passport Number</span>
                                        <span className={styles.detailValue} style={{ color: "#1e3a8a", fontWeight: "700" }}>
                                            {selectedItem.passportNumber}
                                        </span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Date of Issue</span>
                                        <span className={styles.detailValue}>{formatDateOnly(selectedItem.dateOfIssue)}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Date of Expiry</span>
                                        <span className={styles.detailValue}>{formatDateOnly(selectedItem.dateOfExpiry)}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Place of Issue</span>
                                        <span className={styles.detailValue}>{selectedItem.placeOfIssue}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Country of Birth</span>
                                        <span className={styles.detailValue}>{selectedItem.countryOfBirth}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Official Email ID</span>
                                        <span className={styles.detailValue}>{selectedItem.emailId}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Mobile Number</span>
                                        <span className={styles.detailValue}>{selectedItem.mobileNumber}</span>
                                    </div>
                                    <div className={styles.detailItem}>
                                        <span className={styles.detailLabel}>Clearance Status</span>
                                        <span className={styles.detailValue}>{selectedItem.status || "SUBMITTED"}</span>
                                    </div>
                                    <div className={`${styles.detailItem} ${styles.detailFull}`}>
                                        <span className={styles.detailLabel}>Event Details / Affiliation Purpose</span>
                                        <span className={styles.detailValue}>{selectedItem.eventDetails || "IVEC 2027 Conference"}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={styles.modalFooter}>
                            {modalType === "payment" && selectedItem?.paymentStatus === "SUCCESS" && (
                                <button
                                    type="button"
                                    className={styles.btnPrint}
                                    style={{ background: "#059669", color: "#ffffff", borderColor: "#047857" }}
                                    onClick={() => handleReceiptPrint(selectedItem)}
                                >
                                    <FaDownload /> Download Official Receipt (PDF)
                                </button>
                            )}
                            {modalType === "participant" && (
                                <button
                                    type="button"
                                    className={styles.btnPrint}
                                    style={{ background: "#1d4ed8", color: "#ffffff", borderColor: "#1e40af" }}
                                    onClick={() => handleParticipantPdfDownload(selectedItem)}
                                >
                                    <FaFilePdf /> Download Official Clearance PDF
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.btnPrint}
                                onClick={() => window.print()}
                            >
                                <FaPrint /> Print
                            </button>
                            <button
                                type="button"
                                className={styles.btnCloseModal}
                                onClick={() => setSelectedItem(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboardPage;
